import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const CustomizedDot = (props) => {
  const { cx, cy, value } = props;

  if (value > 2500) {
    return (
      <svg
        x={cx - 12}
        y={cy - 12}
        width={24}
        height={24}
        viewBox="0 0 1024 1024"
      >
        <circle cx="512" cy="512" r="480" fill="#ff4d4f" stroke="#fff" strokeWidth="20" />
        <path 
          d="M512 1009.984c-274.912 0-497.76-222.848-497.76-497.76s222.848-497.76 497.76-497.76c274.912 0 497.76 222.848 497.76 497.76s-222.848 497.76-497.76 497.76zM340.768 295.936c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM686.176 296.704c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM772.928 555.392c-18.752-8.864-40.928-0.576-49.632 18.528-40.224 88.576-120.256 143.552-208.832 143.552-85.952 0-164.864-52.64-205.952-137.376-9.184-18.912-31.648-26.592-50.08-17.28-18.464 9.408-21.216 21.472-15.936 32.64 52.8 111.424 155.232 186.784 269.76 186.784 117.984 0 217.12-70.944 269.76-186.784 8.672-19.136 9.568-31.2-9.12-40.096z" 
          fill="white" 
        />
      </svg>
    );
  }

  return (
    <svg
      x={cx - 12}
      y={cy - 12}
      width={24}
      height={24}
      viewBox="0 0 1024 1024"
    >
      <circle cx="512" cy="512" r="480" fill="#52c41a" stroke="#fff" strokeWidth="20" />
      <path 
        d="M517.12 53.248q95.232 0 179.2 36.352t145.92 98.304 98.304 145.92 36.352 179.2-36.352 179.2-98.304 145.92-145.92 98.304-179.2 36.352-179.2-36.352-145.92-98.304-98.304-145.92-36.352-179.2 36.352-179.2 98.304-145.92 145.92-98.304 179.2-36.352zM663.552 261.12q-15.36 0-28.16 6.656t-23.04 18.432-15.872 27.648-5.632 33.28q0 35.84 21.504 61.44t51.2 25.6 51.2-25.6 21.504-61.44q0-17.408-5.632-33.28t-15.872-27.648-23.04-18.432-28.16-6.656zM373.76 261.12q-29.696 0-50.688 25.088t-20.992 60.928 20.992 61.44 50.688 25.6 50.176-25.6 20.48-61.44-20.48-60.928-50.176-25.088zM520.192 602.112q-51.2 0-97.28 9.728t-82.944 27.648-62.464 41.472-35.84 51.2q-1.024 1.024-1.024 2.048-1.024 3.072-1.024 8.704t2.56 11.776 7.168 11.264 12.8 6.144q25.6-27.648 62.464-50.176 31.744-19.456 79.36-35.328t114.176-15.872q67.584 0 116.736 15.872t81.92 35.328q37.888 22.528 63.488 50.176 17.408-5.12 19.968-18.944t0.512-18.944-3.072-7.168-1.024-3.072q-26.624-55.296-100.352-88.576t-176.128-33.28z" 
        fill="white" 
      />
    </svg>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="weekly-tooltip">
        <p className="tooltip-day">{label}</p>
        {payload.map((item, index) => (
          <p key={index} className="tooltip-item" style={{ color: item.color }}>
            {item.name}: {item.value?.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function WeeklyChart(props) {
  const { datas } = props;

  if (!datas || datas.length === 0) {
    return (
      <div className="chart-empty-state">
        <p>No weekly data available</p>
      </div>
    );
  }

  return (
    <div className="weekly-chart-container">
      <div className="chart-header">
        <h3>Weekly Performance Overview</h3>
        <p className="chart-subtitle">Daily sales, tax, discount & net amount trends</p>
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={datas}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e0e0e0"
              vertical={false}
            />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={{ stroke: '#ccc' }}
              tickLine={{ stroke: '#ccc' }}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={(value) => `${value?.toLocaleString()}`}
              width={80}
              axisLine={{ stroke: '#ccc' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: 15 }}
              iconType="circle"
              formatter={(value) => <span style={{ color: '#333', fontSize: 12 }}>{value}</span>}
            />
            <Line
              type="monotone"
              dataKey="Sale"
              stroke="#8884d8"
              strokeWidth={3}
              dot={<CustomizedDot />}
              activeDot={{ r: 8, stroke: "#fff", strokeWidth: 2 }}
              name="Total Sale"
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="SalesTax" 
              stroke="#ffc658"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#ffc658" }}
              name="Sales Tax"
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="Discount" 
              stroke="#ff8042"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#ff8042" }}
              name="Discount"
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="NetAmount" 
              stroke="#82ca9d"
              strokeWidth={3}
              dot={{ r: 5, fill: "#82ca9d", strokeWidth: 2 }}
              name="Net Amount"
              animationDuration={1500}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-footer">
        <div className="legend-info">
          <span className="legend-dot sale"></span>
          <span>Total Sale</span>
          <span className="legend-dot tax"></span>
          <span>Sales Tax</span>
          <span className="legend-dot discount"></span>
          <span>Discount</span>
          <span className="legend-dot net"></span>
          <span>Net Amount</span>
        </div>
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-label">Total Weekly Sale:</span>
            <span className="stat-value">
              {datas.reduce((sum, item) => sum + (item.Sale || 0), 0).toLocaleString()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Weekly Net:</span>
            <span className="stat-value net">
              {datas.reduce((sum, item) => sum + (item.NetAmount || 0), 0).toLocaleString()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Best Day:</span>
            <span className="stat-value">
              {datas.reduce((best, item) => (item.Sale > (best?.Sale || 0) ? item : best), null)?.name || 'N/A'}
            </span>
          </div>
        </div>
      </div>

      <style>{`
        .weekly-chart-container {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }
        
        .weekly-chart-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
        }
        
        .chart-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f0f0f0;
        }
        
        .chart-header h3 {
          margin: 0 0 5px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
        
        .chart-subtitle {
          margin: 0;
          font-size: 12px;
          color: #999;
        }
        
        .chart-wrapper {
          width: 100%;
          overflow-x: auto;
        }
        
        .chart-footer {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #f0f0f0;
        }
        
        .legend-info {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 20px;
          align-items: center;
          font-size: 12px;
          color: #666;
          margin-bottom: 15px;
        }
        
        .legend-dot {
          display: inline-block;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin-right: 5px;
        }
        
        .legend-dot.sale {
          background: #8884d8;
        }
        
        .legend-dot.tax {
          background: #ffc658;
        }
        
        .legend-dot.discount {
          background: #ff8042;
        }
        
        .legend-dot.net {
          background: #82ca9d;
        }
        
        .stats-summary {
          display: flex;
          justify-content: space-around;
          flex-wrap: wrap;
          gap: 20px;
          padding-top: 10px;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
        }
        
        .stat-label {
          font-size: 11px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .stat-value {
          font-size: 16px;
          font-weight: 700;
          color: #8884d8;
        }
        
        .stat-value.net {
          color: #82ca9d;
        }
        
        .chart-empty-state {
          background: white;
          border-radius: 16px;
          padding: 60px 20px;
          text-align: center;
          color: #999;
          font-size: 14px;
        }
        
        /* Global Tooltip Styles */
        :global(.weekly-tooltip) {
          background: rgba(0, 0, 0, 0.85);
          padding: 12px 16px;
          border-radius: 8px;
          color: white;
          min-width: 180px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        :global(.tooltip-day) {
          margin: 0 0 8px 0;
          font-size: 13px;
          font-weight: 600;
          color: #ffd700;
          border-bottom: 1px solid rgba(255,255,255,0.2);
          padding-bottom: 5px;
        }
        
        :global(.tooltip-item) {
          margin: 5px 0;
          font-size: 12px;
          font-weight: 500;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .weekly-chart-container {
            padding: 15px;
          }
          
          .chart-header h3 {
            font-size: 14px;
          }
          
          .legend-info {
            gap: 12px;
            font-size: 10px;
          }
          
          .stats-summary {
            flex-direction: column;
            align-items: center;
            gap: 12px;
          }
          
          .stat-value {
            font-size: 14px;
          }
          
          :global(.recharts-default-legend) {
            display: flex !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
            gap: 10px !important;
          }
        }
        
        @media (max-width: 480px) {
          .legend-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
}

export default WeeklyChart;